/**
 * Inbox Split - Conversation Index Prototype
 *
 * Three-pane layout: Browse rail | Thread feed | Thread reader | Right-rail chat
 *
 * Workspace scoping rules:
 * - Every thread belongs to a workspace
 * - Extensions are enabled per-workspace
 * - Disabling a workspace hides its threads AND removes its extension shortcuts
 * - The Extensions filter tab disappears when no extension-sourced threads are visible
 *
 * Access: http://flownotes.localhost/?test=inbox-split
 */

import { useState, useEffect, useRef } from 'react'
import {
  Mic, Bot, Zap, Lightbulb, Mail, Archive, MessageSquare,
  MoreVertical, Inbox, Search, X, AlignJustify, List, Clock,
  Pin, Folder, Reply, Building2, User, Bell, Play,
  CheckCheck, RefreshCw, ChevronRight, ChevronDown, Plug,
  RotateCcw, Paperclip, Maximize2, Cpu, ToggleLeft, ToggleRight,
  Link2, FileText, Minimize2, EyeOff, BellOff,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import { IconButton } from '@/components/ui/icon-button'

// ---------------------------------------------------------------------------
// Workspace config
// Extensions are per-workspace. Toggling a workspace off hides its threads
// and removes its extension shortcuts from the inbox sidebar.
// ---------------------------------------------------------------------------

interface Workspace {
  id: string
  label: string
  icon: React.ElementType
  active: boolean
  extensions: string[]
  color: string
}

const INITIAL_WORKSPACES: Workspace[] = [
  {
    id: 'personal',
    label: 'Personal',
    icon: User,
    active: true,
    extensions: [],
    color: 'text-rose-400',
  },
  {
    id: 'hop',
    label: 'Hop Studios',
    icon: Building2,
    active: true,
    extensions: ['basecamp', 'google-workspace'],
    color: 'text-blue-400',
  },
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Kind = 'voice' | 'email' | 'agent' | 'suggestion' | 'invite' | 'chat' | 'flow' | 'extension'
type Status = 'unread' | 'read' | 'snoozed' | 'archived'
type KindFilter = Kind | 'all' | 'snoozed' | 'archived'

interface FlowStep {
  label: string
  status: 'done' | 'skipped' | 'error'
  detail?: string
  outputDoc?: string
}

interface Thread {
  id: string
  kind: Kind
  title: string
  preview: string
  source: string
  age: string
  status: Status
  workspaceId: string
  extensionId?: string
  pinned?: boolean
  filed?: boolean
  audioDuration?: string
  agentReplies?: number
  linkedDocs?: string[]
  snoozedUntil?: string
  flowSteps?: FlowStep[]
  flowName?: string
}

// ---------------------------------------------------------------------------
// Mock threads � each thread declares its workspace and (if applicable) extensionId
// ---------------------------------------------------------------------------

const INITIAL_THREADS: Thread[] = [
  {
    id: 'v1', kind: 'voice', workspaceId: 'personal',
    title: 'Thoughts on inbox-as-conversation-index',
    preview: '"so I was thinking about how voice memos should not really be an extension, the inbox is the bucket..."',
    source: 'personal - iPhone', age: '14m', status: 'unread',
    audioDuration: '4:32', agentReplies: 1,
    linkedDocs: ['hop-studios/clients/exida/todos/9690457324/2026-04-20-estimate.md', 'personal/inbox-thoughts.md'],
  },
  {
    id: 'bc1', kind: 'extension', workspaceId: 'hop', extensionId: 'basecamp',
    title: 'New comment on Basecamp todo 8033432827',
    preview: 'Travis: LGTM on the estimate draft -- one question on Phase 2 hours.',
    source: 'basecamp - hop-studios / Bridging Differences hub', age: '25m', status: 'unread',
  },
  {
    id: 'dr1', kind: 'extension', workspaceId: 'hop', extensionId: 'google-workspace',
    title: 'Reply on Google Doc "Silstat estimate"',
    preview: 'Chris commented: Can we fold travel into Phase 1?',
    source: 'google-workspace - hop-studios', age: '40m', status: 'unread',
  },
  {
    id: 'dg1', kind: 'flow', workspaceId: 'hop',
    title: 'Daily digest -- Tue Apr 23',
    preview: 'FlowJob (temporal 9:00): 3 bets moved, 2 new threads, 0 snoozed items due today.',
    source: 'flow-job - scheduled', age: '9:01', status: 'read',
    flowName: 'Daily digest',
    flowSteps: [
      { label: 'Fetch overdue todos from Basecamp', status: 'done', detail: '4 todos fetched' },
      { label: 'Fetch snoozed inbox items due today', status: 'done', detail: '0 items' },
      { label: 'Summarize recent bet movements (NOW/NEXT)', status: 'done', detail: '3 bets moved since yesterday', outputDoc: 'docs/product/roadmap/STATUS-BOARD.md' },
      { label: 'Generate daily digest message', status: 'done', detail: 'Posted to inbox as thread dg1' },
    ],
  },
  {
    id: 'c1', kind: 'chat', workspaceId: 'hop',
    title: 'Refactor voice-memo-manager.tsx',
    preview: 'Agent: I have reverted the 7 files. Want me to start with the canvas mockup or the brainstorm doc first?',
    source: 'flownotes - cursor', age: '8m', status: 'unread', agentReplies: 47,
  },
  {
    id: 's1', kind: 'suggestion', workspaceId: 'personal', pinned: true,
    title: 'You have 3 voice memos older than 30 days',
    preview: 'Want me to summarize them, file each into a workspace, then archive -- or just archive?',
    source: 'auto - agent', age: '3h', status: 'unread',
  },
  {
    id: 'a1', kind: 'flow', workspaceId: 'hop',
    title: 'Parity harness completed: 8 / 10 prompts passed',
    preview: 'Run #14 finished. 2 regressions in folder-scoping. Open the report?',
    source: 'parity-harness - flow', age: '1h', status: 'read',
    flowName: 'Chat parity harness',
    flowSteps: [
      { label: 'Load prompt set (prompts.json)', status: 'done', detail: '10 prompts' },
      { label: 'Run prompts against chat engine', status: 'done', detail: '10 / 10 executed' },
      { label: 'Score responses with scorer.js', status: 'done', detail: '8 passed, 2 failed' },
      { label: 'Generate report', status: 'done', detail: '2 regressions in folder-scoping', outputDoc: 'docs/implementations/core/chat-system/testing/parity-harness/reports/latest.md' },
      { label: 'File report to workspace', status: 'error', detail: 'Target path not found -- manual filing needed' },
    ],
  },
  {
    id: 'e1', kind: 'email', workspaceId: 'hop',
    title: 'Re: Silstat landing page estimate',
    preview: 'From chris@hopstudios.com - forwarded to flownotes@in.flownotes.app',
    source: 'hop-studios - ws-*@in.flownotes.app (routed)', age: '2h', status: 'read',
  },
  {
    id: 'v2', kind: 'voice', workspaceId: 'personal',
    title: 'Walk to coffee shop - scope picker UX',
    preview: '"...the workspace scope picker should always show the active workspace, not default to all..."',
    source: 'personal - iPhone', age: '6h', status: 'read',
    audioDuration: '2:15', agentReplies: 1,
  },
  {
    id: 'sn1', kind: 'voice', workspaceId: 'personal',
    title: 'Talk to Lauren about Q3 marketing plan',
    preview: 'Snoozed until Mon Apr 27 at 9am',
    source: 'personal - iPhone', age: 'Mon 9am', status: 'snoozed',
    audioDuration: '1:48', snoozedUntil: 'Mon Apr 27 at 9am',
  },
  {
    id: 'v3', kind: 'voice', workspaceId: 'personal', filed: true,
    title: 'TD bank mortgage notes - rate offers',
    preview: 'Filed to personal/mortgage-notes-discussion-with-td.md',
    source: 'personal - iPhone', age: '1d', status: 'read', audioDuration: '7:04',
  },
]

// ---------------------------------------------------------------------------
// Kind config
// ---------------------------------------------------------------------------

const KIND_CONFIG: Record<Kind, { label: string; icon: React.ElementType; color: string }> = {
  voice:      { label: 'Voice',       icon: Mic,           color: 'text-purple-400' },
  email:      { label: 'Email',       icon: Mail,          color: 'text-orange-400' },
  agent:      { label: 'Agent',       icon: Bot,           color: 'text-blue-400' },
  suggestion: { label: 'Suggestions', icon: Lightbulb,     color: 'text-yellow-400' },
  invite:     { label: 'Invite',      icon: Lightbulb,     color: 'text-amber-400' },
  chat:       { label: 'Chats',       icon: MessageSquare, color: 'text-sky-400' },
  flow:       { label: 'Flows',       icon: Zap,           color: 'text-green-400' },
  extension:  { label: 'Extensions',  icon: Building2,     color: 'text-teal-400' },
}

// ---------------------------------------------------------------------------
// Sidebar inbox shortcuts
// Extension shortcuts are only shown when their parent workspace is active.
// Default shortcuts (All, Voice, Snoozed, etc.) are always visible.
// ---------------------------------------------------------------------------

interface InboxShortcut {
  id: KindFilter
  label: string
  icon: React.ElementType
  /** If set, only shown when this extension's workspace is active */
  extensionId?: string
  /** Which workspace this extension belongs to */
  workspaceId?: string
}

const DEFAULT_INBOX_SHORTCUTS: InboxShortcut[] = [
  { id: 'all',       label: 'All',        icon: Inbox },
  { id: 'voice',     label: 'Voice',      icon: Mic },
  { id: 'chat',      label: 'Chats',      icon: MessageSquare },
  { id: 'email',     label: 'Email',      icon: Mail },
  { id: 'agent',     label: 'Agent',      icon: Bot },
  { id: 'extension', label: 'Extensions', icon: Building2 },
  { id: 'snoozed',   label: 'Snoozed',    icon: Clock },
  { id: 'archived',  label: 'Archive',    icon: Archive },
]

// Extension-contributed shortcuts � shown only when their workspace is active
const EXTENSION_SHORTCUTS: InboxShortcut[] = [
  { id: 'extension', label: 'Basecamp',        icon: Building2, extensionId: 'basecamp',        workspaceId: 'hop' },
  { id: 'extension', label: 'Google Workspace', icon: Mail,     extensionId: 'google-workspace', workspaceId: 'hop' },
]

// ---------------------------------------------------------------------------
// Snooze options
// ---------------------------------------------------------------------------

const SNOOZE_OPTIONS = [
  { label: 'In 1 hour',        hint: 'Today ~5pm',          value: '1h' },
  { label: 'Later today',      hint: 'Today 7pm',           value: '4h' },
  { label: 'Tomorrow morning', hint: 'Fri Apr 25, 9am',     value: '1d' },
  { label: 'This weekend',     hint: 'Sat Apr 26, 9am',     value: '3d' },
  { label: 'Next Monday',      hint: 'Mon Apr 28, 9am',     value: '7d' },
]

// ---------------------------------------------------------------------------
// Browse rail � workspaces with toggle + extension-aware shortcuts
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// WorkspaceNavSection - scoped Documents + extensions inside each workspace
// Matches webapp pattern: workspace row (chevron = expand, label = select)
// ---------------------------------------------------------------------------

function WorkspaceNavSection({ ws, onToggle }: { ws: Workspace; onToggle: () => void }) {
  const [expanded, setExpanded] = useState(true)
  const WsIcon = ws.icon

  return (
    <div className={cn(!ws.active && 'opacity-40')}>
      {/* Workspace row */}
      <div className="flex items-center gap-1 w-full px-2 py-1.5 rounded-md text-sm text-left">
        <button
          onClick={() => setExpanded(v => !v)}
          className="shrink-0 text-muted-foreground/50 hover:text-foreground transition-colors p-0.5 -ml-0.5 rounded"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded
            ? <ChevronDown className="h-3 w-3" />
            : <ChevronRight className="h-3 w-3" />
          }
        </button>
        <WsIcon className={cn('h-3.5 w-3.5 shrink-0', ws.active ? ws.color : 'text-muted-foreground')} />
        <span className={cn('flex-1 truncate', ws.active ? 'text-foreground' : 'text-muted-foreground')}>
          {ws.label}
        </span>
        {/* Workspace active toggle */}
        <button
          onClick={onToggle}
          title={ws.active ? 'Disable workspace' : 'Enable workspace'}
          className="shrink-0 text-muted-foreground/40 hover:text-foreground transition-colors p-0.5 rounded"
        >
          {ws.active
            ? <ToggleRight className="h-3.5 w-3.5 text-primary" />
            : <ToggleLeft className="h-3.5 w-3.5" />
          }
        </button>
      </div>

      {/* Workspace children: extensions + Documents */}
      {expanded && ws.active && (
        <div className="ml-4 flex flex-col gap-0.5 border-l border-border pl-2 mb-1">
          {ws.extensions.map(extId => (
            <div key={extId} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-muted-foreground">
              <Plug className="h-2.5 w-2.5 shrink-0 opacity-50" />
              <span className="truncate capitalize">{extId.replace(/-/g, ' ')}</span>
            </div>
          ))}
          <button className="flex items-center gap-1.5 w-full px-2 py-1 rounded-md text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-left">
            <ChevronRight className="h-2.5 w-2.5 shrink-0 opacity-40" />
            <FileText className="h-2.5 w-2.5 shrink-0 opacity-50" />
            <span className="truncate">Documents</span>
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Browse rail
// ---------------------------------------------------------------------------

function BrowseRail({
  threads,
  workspaces,
  activeNav,
  activeFilter,
  activeExtension,
  onNavChange,
  onFilterChange,
  onExtensionChange,
  onToggleWorkspace,
}: {
  threads: Thread[]
  workspaces: Workspace[]
  activeNav: string
  activeFilter: KindFilter
  activeExtension: string | null
  onNavChange: (id: string) => void
  onFilterChange: (f: KindFilter) => void
  onExtensionChange: (id: string | null) => void
  onToggleWorkspace: (id: string) => void
}) {
  const [inboxExpanded, setInboxExpanded] = useState(true)
  const [extensionsExpanded, setExtensionsExpanded] = useState(true)

  const activeWsIds = new Set(workspaces.filter(w => w.active).map(w => w.id))
  const activeExtIds = new Set(
    workspaces.filter(w => w.active).flatMap(w => w.extensions)
  )

  // Only count threads from active workspaces
  const unreadCounts: Partial<Record<Kind, number>> = {}
  for (const t of threads) {
    if (!activeWsIds.has(t.workspaceId)) continue
    if (t.status === 'unread') unreadCounts[t.kind] = (unreadCounts[t.kind] ?? 0) + 1
  }
  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0)
  const snoozedCount = threads.filter(t => activeWsIds.has(t.workspaceId) && t.status === 'snoozed').length
  const archivedCount = threads.filter(t => t.status === 'archived').length

  function shortcutCount(id: KindFilter): number | undefined {
    if (id === 'all') return totalUnread || undefined
    if (id === 'snoozed') return snoozedCount || undefined
    if (id === 'archived') return archivedCount || undefined
    return unreadCounts[id as Kind] || undefined
  }

  // Extension shortcuts filtered to active workspaces
  const visibleExtShortcuts = EXTENSION_SHORTCUTS.filter(
    sc => sc.workspaceId && activeWsIds.has(sc.workspaceId)
  )

  // Extension thread counts per extensionId
  const extCounts: Record<string, number> = {}
  for (const t of threads) {
    if (!activeWsIds.has(t.workspaceId)) continue
    if (t.kind === 'extension' && t.extensionId && t.status === 'unread') {
      extCounts[t.extensionId] = (extCounts[t.extensionId] ?? 0) + 1
    }
  }
  const totalExtCount = Object.values(extCounts).reduce((a, b) => a + b, 0)

  // "Extensions" generic shortcut only shown if any extension threads exist in active workspaces
  const hasExtensionThreads = threads.some(
    t => t.kind === 'extension' && activeWsIds.has(t.workspaceId) && t.status !== 'archived'
  )

  // Default shortcuts � Extensions is handled separately as an expandable parent
  const visibleDefaults = DEFAULT_INBOX_SHORTCUTS.filter(sc => {
    if (sc.id === 'extension') return false // rendered as expandable separately
    return true
  })

  return (
    <div
      data-testid="inbox-browse-nav"
      className="flex flex-col w-48 shrink-0 border-r border-border bg-background overflow-hidden"
    >
      {/* App wordmark */}
      <div className="flex items-center gap-2 px-3 h-10 border-b border-border">
        <div className="w-4 h-4 rounded bg-primary/20 flex items-center justify-center">
          <span className="text-[9px] font-bold text-primary leading-none">F</span>
        </div>
        <span className="text-sm font-semibold text-foreground flex-1">FlowNotes</span>
        {/* Active workspace count badge */}
        <span className="text-[9px] font-bold rounded-full px-1.5 py-0.5 bg-primary text-primary-foreground leading-none">
          {workspaces.filter(w => w.active).length}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 py-1">
        {/* Home */}
        <div className="px-2">
          <button
            onClick={() => onNavChange('home')}
            className={cn(
              'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm transition-colors text-left',
              activeNav === 'home' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Inbox className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="flex-1">Home</span>
          </button>
        </div>

        {/* Inbox expandable */}
        <div className="px-2 mt-0.5">
          <div className={cn(
            'flex items-center gap-0 w-full rounded-md text-sm transition-colors',
            activeNav === 'inbox' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}>
            {/* Chevron: toggle expand only */}
            <button
              onClick={() => setInboxExpanded(v => !v)}
              className="shrink-0 p-1.5 rounded-l-md hover:bg-muted/60 transition-colors"
              aria-label={inboxExpanded ? 'Collapse inbox' : 'Expand inbox'}
            >
              {inboxExpanded
                ? <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                : <ChevronRight className="h-3.5 w-3.5 opacity-50" />
              }
            </button>
            {/* Label: navigate only */}
            <button
              onClick={() => onNavChange('inbox')}
              className="flex-1 flex items-center gap-2 py-1.5 pr-2 text-left min-w-0"
            >
              <span className="flex-1 truncate">Inbox</span>
              {totalUnread > 0 && (
                <span className={cn('text-[9px] font-bold rounded-full px-1 leading-4 min-w-[14px] text-center shrink-0',
                  activeNav === 'inbox' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                )}>
                  {totalUnread}
                </span>
              )}
            </button>
          </div>

          {inboxExpanded && (
            <div className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-border pl-2">
              {visibleDefaults.map((sc, i) => {
                const Icon = sc.icon
                const count = shortcutCount(sc.id)
                const active = activeNav === 'inbox' && activeFilter === sc.id
                return (
                  <button
                    key={`${sc.id}-${i}`}
                    onClick={() => { onNavChange('inbox'); onFilterChange(sc.id); onExtensionChange(null) }}
                    className={cn(
                      'flex items-center gap-1.5 w-full px-2 py-1 rounded-md text-xs transition-colors text-left',
                      active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    <Icon className="h-3 w-3 shrink-0 opacity-70" />
                    <span className="flex-1 truncate">{sc.label}</span>
                    {count != null && (
                      <span className={cn('text-[9px] tabular-nums', active ? 'text-primary' : 'text-muted-foreground/60')}>
                        {count}
                      </span>
                    )}
                  </button>
                )
              })}

              {/* Extensions - expandable parent with per-extension children */}
              {hasExtensionThreads && (
                <>
                  <div className={cn(
                    'flex items-center gap-0 w-full rounded-md text-xs transition-colors',
                    activeNav === 'inbox' && activeFilter === 'extension'
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}>
                    {/* Chevron: expand only */}
                    <button
                      onClick={() => setExtensionsExpanded(v => !v)}
                      className="shrink-0 p-1 rounded-l-md hover:bg-muted/60 transition-colors"
                      aria-label={extensionsExpanded ? 'Collapse extensions' : 'Expand extensions'}
                    >
                      {extensionsExpanded
                        ? <ChevronDown className="h-3 w-3 opacity-50" />
                        : <ChevronRight className="h-3 w-3 opacity-50" />
                      }
                    </button>
                    {/* Label: select extensions filter */}
                    <button
                      onClick={() => { onNavChange('inbox'); onFilterChange('extension'); onExtensionChange(null) }}
                      className="flex-1 flex items-center gap-1.5 py-1 pr-2 text-left min-w-0"
                    >
                      <span className="flex-1 truncate">Extensions</span>
                      {totalExtCount > 0 && (
                        <span className={cn('text-[9px] tabular-nums shrink-0',
                          activeNav === 'inbox' && activeFilter === 'extension' ? 'text-primary' : 'text-muted-foreground/60'
                        )}>
                          {totalExtCount}
                        </span>
                      )}
                    </button>
                  </div>


                  {extensionsExpanded && (
                    <div className="ml-3 flex flex-col gap-0.5 border-l border-border pl-2">
                      {visibleExtShortcuts.map((sc) => {
                        const Icon = sc.icon
                        const count = sc.extensionId ? (extCounts[sc.extensionId] ?? 0) : 0
                        const active = activeNav === 'inbox' && activeFilter === 'extension' && activeExtension === sc.extensionId
                        return (
                          <button
                            key={sc.extensionId}
                            onClick={() => {
                              onNavChange('inbox')
                              onFilterChange('extension')
                              onExtensionChange(sc.extensionId ?? null)
                            }}
                            className={cn(
                              'flex items-center gap-1.5 w-full px-2 py-1 rounded-md text-xs transition-colors text-left',
                              active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                          >
                            <Plug className="h-2.5 w-2.5 shrink-0 opacity-50" />
                            <span className="flex-1 truncate">{sc.label}</span>
                            {count > 0 && (
                              <span className={cn('text-[9px] tabular-nums', active ? 'text-primary' : 'text-muted-foreground/60')}>
                                {count}
                              </span>
                            )}
                          </button>
                        )
                      })}
                      {/* Hint when extension shortcuts are hidden due to inactive workspace */}
                      {EXTENSION_SHORTCUTS.some(sc => sc.workspaceId && !activeWsIds.has(sc.workspaceId)) && (
                        <div className="px-2 py-0.5">
                          <p className="text-[9px] text-muted-foreground/40 leading-snug">
                            Some hidden (workspace inactive)
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Workspaces */}
        <div className="mt-2 px-3 mb-1 flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/50 font-semibold flex-1">
            Workspaces
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-2">
          {workspaces.map(ws => {
            const WsIcon = ws.icon
            return (
              <WorkspaceNavSection
                key={ws.id}
                ws={ws}
                onToggle={() => onToggleWorkspace(ws.id)}
              />
            )
          })}
        </div>
      </div>

      {/* Bottom */}
      <div className="px-2 pb-2 pt-2 flex flex-col gap-0.5 border-t border-border">
        <button className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-left">
          <User className="h-3.5 w-3.5 shrink-0" />
          <span>Settings</span>
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Workspace scope indicator (shown in feed header when not all workspaces active)
// ---------------------------------------------------------------------------

function WorkspaceScopePill({ workspaces }: { workspaces: Workspace[] }) {
  const active = workspaces.filter(w => w.active)
  const inactive = workspaces.filter(w => !w.active)
  if (inactive.length === 0) return null

  return (
    <div className="flex items-center gap-1 px-3 py-1 border-b border-amber-500/20 bg-amber-500/5 shrink-0">
      <span className="text-[10px] text-amber-600/80 flex-1">
        Showing {active.map(w => w.label).join(', ')} only
        {inactive.length > 0 && ` -- ${inactive.map(w => w.label).join(', ')} disabled`}
      </span>
      <span className="text-[10px] text-amber-500/60">
        {inactive.length} workspace{inactive.length !== 1 ? 's' : ''} hidden
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Kind filter tabs � only show tabs with threads in active workspaces
// ---------------------------------------------------------------------------

function KindTabs({
  filter,
  onChange,
  threads,
  activeWsIds,
}: {
  filter: KindFilter
  onChange: (f: KindFilter) => void
  threads: Thread[]
  activeWsIds: Set<string>
}) {
  // Only count threads from active workspaces
  const scopedThreads = threads.filter(t => activeWsIds.has(t.workspaceId))

  const counts: Partial<Record<Kind, number>> = {}
  for (const t of scopedThreads) {
    if (t.status === 'snoozed' || t.status === 'archived') continue
    if (t.status === 'unread') counts[t.kind] = (counts[t.kind] ?? 0) + 1
  }
  const totalUnread = Object.values(counts).reduce((a, b) => a + b, 0)
  const snoozedCount = scopedThreads.filter(t => t.status === 'snoozed').length
  const archivedCount = threads.filter(t => t.status === 'archived').length

  type TabDef = { id: KindFilter; label: string; icon?: React.ElementType; count?: number }
  const tabs: TabDef[] = [
    { id: 'all', label: 'All', count: totalUnread },
    ...Object.entries(KIND_CONFIG)
      .filter(([k]) => scopedThreads.some(t => t.kind === (k as Kind) && t.status !== 'archived'))
      .map(([k, meta]) => ({ id: k as KindFilter, label: meta.label, icon: meta.icon, count: counts[k as Kind] })),
    { id: 'snoozed',  label: 'Snoozed', icon: Clock,   count: snoozedCount },
    { id: 'archived', label: 'Archive',  icon: Archive, count: archivedCount },
  ]

  return (
    <div className="flex items-center gap-1 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
      {tabs.map((tab, i) => {
        const Icon = tab.icon
        const active = filter === tab.id
        return (
          <button
            key={`${tab.id}-${i}`}
            onClick={() => onChange(tab.id)}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors shrink-0 border',
              active ? 'bg-primary/10 text-primary border-primary/20' : 'text-muted-foreground hover:bg-muted hover:text-foreground border-transparent'
            )}
          >
            {Icon && <Icon className="h-3 w-3 shrink-0" />}
            <span>{tab.label}</span>
            {tab.count != null && tab.count > 0 && (
              <span className={cn('text-[9px] font-bold rounded-full px-1 leading-4 min-w-[13px] text-center',
                active ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/20 text-muted-foreground'
              )}>
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared thread actions menu
// Used in: ThreadRow hover, ReaderPane header
// ---------------------------------------------------------------------------

function ThreadActionsMenu({
  threadId, onSnooze, onArchive, onOpenInChat, compact = false,
}: {
  threadId: string
  onSnooze: (id: string, label: string, hint: string) => void
  onArchive: (id: string) => void
  onOpenInChat: (id: string) => void
  compact?: boolean
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors',
            compact ? 'p-1' : 'p-1.5 border border-border'
          )}
          title="More actions"
        >
          <MoreVertical className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Snooze submenu */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="flex items-center gap-2 text-sm">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span>Snooze</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-52">
            <DropdownMenuLabel className="text-[11px] text-muted-foreground/70 font-normal px-2 py-1">
              Snooze until...
            </DropdownMenuLabel>
            {SNOOZE_OPTIONS.map(opt => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => onSnooze(threadId, opt.label, opt.hint)}
                className="flex items-center justify-between gap-4 text-sm"
              >
                <span>{opt.label}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{opt.hint}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => onArchive(threadId)}
          className="flex items-center gap-2 text-sm"
        >
          <Archive className="h-3.5 w-3.5 text-muted-foreground" />
          <span>Archive</span>
        </DropdownMenuItem>

        <DropdownMenuItem className="flex items-center gap-2 text-sm">
          <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
          <span>Mark as unread</span>
        </DropdownMenuItem>

        <DropdownMenuItem className="flex items-center gap-2 text-sm">
          <BellOff className="h-3.5 w-3.5 text-muted-foreground" />
          <span>Mute thread</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => onOpenInChat(threadId)}
          className="flex items-center gap-2 text-sm"
        >
          <Maximize2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span>Open in chat</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ---------------------------------------------------------------------------
// Thread row
// ---------------------------------------------------------------------------

function ThreadRow({
  thread, active, dense, onClick, onArchive, onSnooze, workspace,
}: {
  thread: Thread
  active: boolean
  dense: boolean
  onClick: () => void
  onArchive: (id: string) => void
  onSnooze: (id: string, label: string, hint: string) => void
  workspace?: Workspace
}) {
  const isUnread = thread.status === 'unread'
  const isSnoozed = thread.status === 'snoozed'
  const isArchived = thread.status === 'archived'
  const cfg = KIND_CONFIG[thread.kind]
  const Icon = cfg.icon

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex items-start gap-2 cursor-pointer border-b border-border transition-colors select-none group border-l-2',
        dense ? 'px-3 py-1.5' : 'px-3 py-2.5',
        active ? 'bg-primary/5 border-l-primary' : 'hover:bg-muted/40 border-l-transparent',
        isArchived && 'opacity-60',
      )}
    >
      <div className="w-2 shrink-0 flex justify-center pt-1.5">
        {isUnread && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
      </div>
      <div className={cn('shrink-0 mt-0.5', isSnoozed || isArchived ? 'text-muted-foreground/40' : cfg.color)}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {thread.pinned && <Pin className="h-2.5 w-2.5 text-primary shrink-0" />}
          <span className={cn('text-sm truncate flex-1', isUnread ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
            {thread.title}
          </span>
          {thread.audioDuration && (
            <span className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground font-mono shrink-0 leading-none">
              {thread.audioDuration}
            </span>
          )}
          {thread.agentReplies != null && thread.agentReplies > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 leading-none">
              {thread.agentReplies}
            </span>
          )}
        </div>
        {!dense && (
          <>
            {isSnoozed && thread.snoozedUntil ? (
              <p className="text-xs text-amber-500/70 truncate mt-0.5 flex items-center gap-1">
                <Clock className="h-2.5 w-2.5 shrink-0" /> Wakes up {thread.snoozedUntil}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground truncate mt-0.5 leading-snug">{thread.preview}</p>
            )}
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[10px] text-muted-foreground/50 truncate">{thread.source}</span>
              {thread.filed && <Folder className="h-2.5 w-2.5 text-muted-foreground/50 shrink-0" />}
              {/* Workspace tag */}
              {workspace && (
                <span className={cn('text-[9px] shrink-0', workspace.color, 'opacity-60')}>{workspace.label}</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Hover actions */}
      {!isArchived && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" onClick={e => e.stopPropagation()}>
          <ThreadActionsMenu
            threadId={thread.id}
            onSnooze={onSnooze}
            onArchive={onArchive}
            onOpenInChat={() => {}}
            compact
          />
        </div>
      )}

      {!isArchived && (
        <span className="text-[10px] text-muted-foreground/50 shrink-0 whitespace-nowrap mt-0.5 group-hover:hidden">
          {thread.age}
        </span>
      )}
      {isArchived && (
        <span className="text-[10px] text-muted-foreground/40 shrink-0 whitespace-nowrap mt-0.5">
          {thread.age}
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

function SnoozeBanner({ label, hint, onUndo }: { label: string; hint: string; onUndo: () => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border-b border-amber-500/20 shrink-0">
      <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      <span className="text-xs text-amber-600 flex-1">Snoozed: {label} ({hint})</span>
      <button onClick={onUndo} className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 transition-colors">
        <RotateCcw className="h-3 w-3" /> Undo
      </button>
    </div>
  )
}

function ArchiveBanner({ onUndo }: { onUndo: () => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-muted/60 border-b border-border shrink-0">
      <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground flex-1">Thread archived</span>
      <button onClick={onUndo} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <RotateCcw className="h-3 w-3" /> Undo
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Reader pane
// ---------------------------------------------------------------------------

function ReaderPane({
  thread, onArchive, onSnooze, onUnarchive, chatLinkedThreadId, onOpenInChat,
}: {
  thread: Thread
  onArchive: (id: string) => void
  onSnooze: (id: string, label: string, hint: string) => void
  onUnarchive: (id: string) => void
  chatLinkedThreadId: string | null
  onOpenInChat: (threadId: string) => void
}) {
  const cfg = KIND_CONFIG[thread.kind]
  const Icon = cfg.icon
  const isArchived = thread.status === 'archived'
  const isSnoozed = thread.status === 'snoozed'
  const chatIsLinkedHere = chatLinkedThreadId === thread.id
  const [stepsExpanded, setStepsExpanded] = useState(true)

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">
      {isArchived && (
        <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border shrink-0">
          <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground flex-1">This thread is archived</span>
          <button onClick={() => onUnarchive(thread.id)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <RotateCcw className="h-3 w-3" /> Move to inbox
          </button>
        </div>
      )}
      {isSnoozed && thread.snoozedUntil && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 shrink-0">
          <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          <span className="text-xs text-amber-600 flex-1">Snoozed until {thread.snoozedUntil}</span>
          <button onClick={() => onUnarchive(thread.id)} className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 transition-colors">
            <RotateCcw className="h-3 w-3" /> Wake up now
          </button>
        </div>
      )}

      <div className="flex items-start gap-3 px-4 py-3 border-b border-border shrink-0">
        <div className={cn('shrink-0 mt-0.5', cfg.color)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-foreground leading-tight">{thread.title}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{thread.source}</p>
        </div>
        {!isArchived && (
          <div className="flex items-center gap-1 shrink-0">
            <button className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-border text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <Reply className="h-3 w-3" /> Reply
            </button>
            <ThreadActionsMenu
              threadId={thread.id}
              onSnooze={onSnooze}
              onArchive={onArchive}
              onOpenInChat={onOpenInChat}
            />
          </div>
        )}
      </div>

      {thread.linkedDocs && thread.linkedDocs.length > 0 && (
        <div className="px-4 py-2 border-b border-border bg-muted/20 shrink-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">Linked docs</p>
          {thread.linkedDocs.map(d => (
            <span key={d} className="block text-xs text-muted-foreground font-mono truncate">{d}</span>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4 space-y-4">
        {thread.kind === 'flow' && thread.flowSteps ? (
          <>
            {/* Flow summary message */}
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded bg-green-500/20 text-green-400 flex items-center justify-center shrink-0">
                <Zap className="h-3 w-3" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-semibold text-green-400">{thread.flowName ?? 'Flow'}</span>
                  <span className="text-[10px] text-muted-foreground">{thread.age}</span>
                </div>
                <div className="rounded border border-border bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground leading-relaxed">{thread.preview}</p>
                </div>
              </div>
            </div>

            {/* Collapsible step log */}
            <div className="rounded border border-border overflow-hidden">
              <button
                onClick={() => setStepsExpanded(v => !v)}
                className="flex items-center gap-2 w-full px-3 py-2 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
              >
                {stepsExpanded
                  ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                  : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                }
                <span className="text-xs font-medium text-foreground flex-1">
                  Execution steps ({thread.flowSteps.length})
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {thread.flowSteps.filter(s => s.status === 'done').length}/{thread.flowSteps.length} done
                  {thread.flowSteps.some(s => s.status === 'error') && (
                    <span className="ml-1 text-red-400">1 error</span>
                  )}
                </span>
              </button>

              {stepsExpanded && (
                <div className="divide-y divide-border">
                  {thread.flowSteps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2.5 px-3 py-2">
                      <span className={cn(
                        'shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold',
                        step.status === 'done'    && 'bg-green-500/20 text-green-400',
                        step.status === 'skipped' && 'bg-muted text-muted-foreground',
                        step.status === 'error'   && 'bg-red-500/20 text-red-400',
                      )}>
                        {step.status === 'done' ? '✓' : step.status === 'error' ? '!' : '-'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground leading-snug">{step.label}</p>
                        {step.detail && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">{step.detail}</p>
                        )}
                        {step.outputDoc && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-primary/70 font-mono">
                            <FileText className="h-2.5 w-2.5 shrink-0" />
                            {step.outputDoc.split('/').pop()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Flow actions */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={testIds.inboxSplitPrototype.readerViewFlowButton}
                onClick={() => {
                  const label = thread.flowName ?? thread.title
                  window.location.href = `/?test=flow-editor&flow=${encodeURIComponent(label)}&sourceThread=${encodeURIComponent(thread.id)}`
                }}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-border text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <Zap className="h-3 w-3" /> View flow
              </button>
              <button className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-border text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                <RefreshCw className="h-3 w-3" /> Re-run
              </button>
            </div>
          </>
        ) : thread.kind === 'voice' ? (
          <>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded bg-purple-500/20 text-purple-400 flex items-center justify-center text-[10px] font-bold shrink-0">Y</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-semibold text-purple-400">You - voice</span>
                  <span className="text-[10px] text-muted-foreground">{thread.age}</span>
                </div>
                <div className="rounded border border-border bg-muted/40 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <button className="text-primary shrink-0"><Play className="h-3.5 w-3.5 fill-current" /></button>
                    <div className="flex-1 h-1 bg-border rounded-full relative">
                      <div className="absolute left-0 top-0 bottom-0 w-[32%] bg-primary rounded-full" />
                    </div>
                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">1:28 / {thread.audioDuration ?? '4:32'}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">"{thread.preview.replace(/^"|"$/g, '')}"</p>
                  <p className="text-[10px] text-muted-foreground/50 italic">Transcript - auto-generated</p>
                </div>
              </div>
            </div>
            {thread.agentReplies != null && thread.agentReplies > 0 && (
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold shrink-0">A</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-semibold text-blue-400">Agent - analysis</span>
                    <span className="text-[10px] text-muted-foreground">13m ago</span>
                  </div>
                  <div className="rounded border border-border bg-muted/40 p-3 space-y-2">
                    <p className="text-xs font-semibold text-foreground">Summary</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      You're proposing that <strong className="text-foreground">Inbox</strong> becomes the universal conversation index. Voice memos, forwarded emails, agent pings, and chats all become the same shape - a thread - rendered in different chrome.
                    </p>
                    <div className="mt-2 space-y-0.5">
                      <p className="text-xs font-semibold text-foreground">Suggested next moves</p>
                      <p className="text-xs text-muted-foreground">- Capture as brainstorm doc in docs/product/brainstorms/</p>
                      <p className="text-xs text-muted-foreground">- Build static UI prototype before any schema work</p>
                      <p className="text-xs text-muted-foreground">- Threads schema migration: no legacy shim (see bets)</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex gap-3">
            <div className="w-6 h-6 rounded bg-muted flex items-center justify-center text-[10px] font-bold shrink-0 text-muted-foreground">
              {thread.source[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-semibold text-foreground">{thread.source}</span>
                <span className="text-[10px] text-muted-foreground">{thread.age}</span>
              </div>
              <div className="rounded border border-border bg-muted/40 p-3">
                <p className="text-xs text-muted-foreground leading-relaxed">{thread.preview}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {!isArchived && (
        <div className="px-4 py-3 border-t border-border shrink-0 bg-background space-y-2">
          {chatIsLinkedHere ? (
            /* ?? Handoff state: chat is active for this thread ?? */
            <div className="rounded border border-primary/20 bg-primary/5 px-3 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse shrink-0" />
                <span className="text-xs text-primary font-medium flex-1">Agent chat is active for this thread</span>
                <button
                  onClick={() => onOpenInChat(thread.id)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded border border-primary/30 text-xs text-primary hover:bg-primary/10 transition-colors"
                >
                  <Link2 className="h-3 w-3" /> View chat
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground/60 leading-snug">
                The inbox reply box is paused while the chat panel holds focus on this thread.
                Replies sent from chat appear in this thread too.
              </p>
              <button
                onClick={() => onOpenInChat('')}
                className="text-[10px] text-muted-foreground/60 hover:text-foreground transition-colors underline-offset-2 hover:underline"
              >
                Take back � reply from inbox instead
              </button>
            </div>
          ) : (
            /* ?? Normal composer ?? */
            <>
              <div className="rounded border border-border bg-muted/20 px-3 py-2 min-h-[52px] flex flex-col gap-1 cursor-text">
                <span className="text-xs text-muted-foreground/50 select-none">
                  Reply - or @ mention the agent to take action on this thread
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="flex items-center gap-1 px-2 py-0.5 rounded border border-primary/20 bg-primary/5 text-xs text-primary shrink-0">
                  <Bot className="h-2.5 w-2.5" /> Voice analyzer
                </span>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded border border-border text-xs text-muted-foreground shrink-0">personal</span>
                <button className="p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors" title="Attach">
                  <Paperclip className="h-3.5 w-3.5" />
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => onOpenInChat(thread.id)}
                  className="flex items-center gap-1 px-2 py-1 rounded border border-border text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <Maximize2 className="h-3 w-3" /> Open in chat
                </button>
                <span className="text-[10px] text-muted-foreground/40">Cmd+Return</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Right-rail chat panel
// ---------------------------------------------------------------------------

function RightRailChat({
  thread, chatLinkedThreadId, onOpenInChat, onFocusInbox,
}: {
  thread: Thread
  chatLinkedThreadId: string | null
  onOpenInChat: (threadId: string) => void
  onFocusInbox: () => void
}) {
  const [open, setOpen] = useState(true)
  const isLinked = chatLinkedThreadId === thread.id

  function handleClose() {
    setOpen(false)
    onOpenInChat('') // de-link when closing
  }

  if (!open) {
    return (
      <div data-testid="inbox-agent-chat" data-state="collapsed" className="flex flex-col items-center w-10 shrink-0 border-l border-border bg-background py-3 gap-3">
        <button onClick={() => setOpen(true)} className="p-1.5 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors" title="Open agent chat">
          <MessageSquare className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <div data-testid="inbox-agent-chat" data-state="open" className="flex flex-col w-60 shrink-0 border-l border-border bg-background overflow-hidden">
      <div className="flex items-center gap-2 px-3 h-10 border-b border-border shrink-0">
        <Bot className="h-3.5 w-3.5 text-primary shrink-0" />
        <span className="text-sm font-medium text-foreground flex-1">Agent chat</span>
        <span className="text-[10px] text-muted-foreground/50 shrink-0">focus</span>
        <button onClick={handleClose} className="text-muted-foreground/50 hover:text-foreground transition-colors">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Thread context chip with "Open in inbox" cross-reference */}
      <div className={cn(
        'px-3 py-2 border-b shrink-0',
        isLinked ? 'border-primary/20 bg-primary/5' : 'border-border bg-muted/20'
      )}>
        <div className="flex items-center gap-1.5">
          <Cpu className="h-3 w-3 text-muted-foreground/50 shrink-0" />
          <span className="text-[10px] text-muted-foreground truncate flex-1">
            {thread.title.slice(0, 26)}...
          </span>
          {/* Cross-reference back to inbox */}
          <button
            onClick={onFocusInbox}
            className="flex items-center gap-1 text-[10px] text-primary hover:underline shrink-0"
            title="Jump to this thread in the inbox"
          >
            <Inbox className="h-2.5 w-2.5" /> inbox
          </button>
        </div>
        {isLinked ? (
          <p className="text-[10px] text-primary/60 mt-0.5 leading-snug">
            Inbox reply paused - replies sent here appear in the thread
          </p>
        ) : (
          <p className="text-[10px] text-muted-foreground/40 mt-0.5 leading-snug">
            Same thread as reader - focus chrome for multi-turn work
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 px-3 py-3 space-y-3">
        <div className="flex gap-2 justify-end">
          <div className="max-w-[85%] rounded-lg rounded-tr-sm bg-primary/10 border border-primary/20 px-3 py-2">
            <p className="text-xs text-foreground">Summarize this and file it to the correct workspace.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="w-5 h-5 rounded bg-blue-500/20 text-blue-400 flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5">A</div>
          <div className="flex-1 space-y-1.5">
            <div className="rounded-lg rounded-tl-sm bg-muted/60 border border-border px-3 py-2">
              <p className="text-xs text-foreground font-medium mb-1">Done. Filed to <span className="text-primary">personal/inbox-thoughts.md</span></p>
              <p className="text-xs text-muted-foreground leading-relaxed">Summary: you're proposing that Voice Memos should stop being a standalone extension and route into a unified inbox.</p>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-muted-foreground/50">2 actions taken</span>
              <button className="text-[10px] text-primary hover:underline">view log</button>
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <div className="max-w-[85%] rounded-lg rounded-tr-sm bg-primary/10 border border-primary/20 px-3 py-2">
            <p className="text-xs text-foreground">Create a bet doc from this brainstorm.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="w-5 h-5 rounded bg-blue-500/20 text-blue-400 flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5">A</div>
          <div className="rounded-lg rounded-tl-sm bg-muted/60 border border-border px-3 py-2 flex-1">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-xs text-muted-foreground">Writing bet doc...</span>
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 py-2.5 border-t border-border shrink-0 space-y-1.5">
        <div className="rounded border border-border bg-muted/20 px-2.5 py-2 min-h-[44px] cursor-text">
          <span className="text-xs text-muted-foreground/40">Ask the agent, or @ a tool...</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-primary/20 bg-primary/5 text-[10px] text-primary shrink-0">
            <Bot className="h-2 w-2" /> auto
          </span>
          <span className="text-[10px] text-muted-foreground/50 shrink-0">personal</span>
          <button className="p-1 rounded text-muted-foreground/50 hover:text-foreground transition-colors">
            <Paperclip className="h-3 w-3" />
          </button>
          <div className="flex-1" />
          <span className="text-[10px] text-muted-foreground/40">Cmd+Return</span>
        </div>
      </div>

      <div className="px-3 pb-2 shrink-0">
        <div className="rounded border border-border bg-muted/20 px-2.5 py-1.5 space-y-0.5">
          <p className="text-[10px] text-muted-foreground/60 font-medium">Inbox reply vs. agent chat</p>
          <p className="text-[10px] text-muted-foreground/50 leading-snug">Reply: 1-2 turns, async - "summarize", "file this"</p>
          <p className="text-[10px] text-muted-foreground/50 leading-snug">Chat: multi-turn focus - "build X from this", "draft a bet"</p>
          <p className="text-[10px] text-muted-foreground/50 leading-snug mt-0.5">Same thread. Different chrome.</p>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mock tab bar
//
// Model:
//   - Inbox tab sits OUTSIDE workspace groups - closeable, workspace-agnostic.
//   - A thin separator divides it from workspace tab groups (HS, P).
//   - Navigating from inbox (doc click) opens in the appropriate workspace group;
//     it does NOT replace the inbox tab.
//   - When inbox is closed it can be re-opened from the sidebar or a ghost button.
// ---------------------------------------------------------------------------

const WS_TABS = [
  // Hop Studios group
  { id: 'todos',    label: 'Todos',         wsLabel: 'HS', wsColor: 'bg-indigo-500' },
  { id: 'develop',  label: 'Develop pr...', wsLabel: 'HS', wsColor: 'bg-indigo-500' },
  // Personal group
  { id: 'mortgage', label: 'Mortgage n...', wsLabel: 'P',  wsColor: 'bg-rose-500' },
  { id: 'roast',    label: 'Outside Ro...', wsLabel: 'P',  wsColor: 'bg-rose-500' },
  { id: 'windows',  label: 'Modern Wi...',  wsLabel: 'P',  wsColor: 'bg-rose-500' },
]

function MockTabBar({ activeTabId, onTabChange }: { activeTabId: string; onTabChange: (id: string) => void }) {
  const [inboxOpen, setInboxOpen] = useState(true)

  function closeInbox() {
    setInboxOpen(false)
    if (activeTabId === 'inbox') onTabChange(WS_TABS[0].id)
  }

  return (
    <div
      data-testid="inbox-tab-bar"
      className="flex items-stretch h-9 border-b border-border bg-background shrink-0 overflow-x-auto"
      style={{ scrollbarWidth: 'none' }}
    >
      {/* Inbox tab (workspace-agnostic zone) */}
      {inboxOpen ? (
        <div
          onClick={() => onTabChange('inbox')}
          className={cn(
            'group flex items-center gap-1.5 px-3 cursor-pointer shrink-0 relative transition-colors text-xs select-none',
            activeTabId === 'inbox'
              ? 'bg-background text-foreground border-b-2 border-b-primary -mb-px'
              : 'bg-muted/30 text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Inbox className="h-3 w-3 shrink-0 text-primary/70" />
          <span className="truncate max-w-[80px]">Inbox</span>
          <button
            onClick={e => { e.stopPropagation(); closeInbox() }}
            className="ml-0.5 opacity-0 group-hover:opacity-60 hover:!opacity-100 text-muted-foreground hover:text-foreground transition-opacity shrink-0"
            aria-label="Close inbox tab"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => { setInboxOpen(true); onTabChange('inbox') }}
          className="flex items-center gap-1 px-2 text-muted-foreground/40 hover:text-muted-foreground transition-colors text-xs shrink-0"
          title="Open Inbox"
        >
          <Inbox className="h-3 w-3" />
        </button>
      )}

      {/* Separator: inbox zone / workspace groups */}
      <div className="w-px my-1.5 bg-border/60 shrink-0 mx-0.5" />

      {/* Workspace tab groups */}
      {WS_TABS.map((tab, i) => {
        const active = tab.id === activeTabId
        const showWsBadge = tab.wsLabel !== WS_TABS[i - 1]?.wsLabel
        return (
          <div
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'group flex items-center gap-1.5 px-3 cursor-pointer border-r border-border shrink-0 relative transition-colors text-xs select-none',
              active
                ? 'bg-background text-foreground border-b-2 border-b-primary -mb-px'
                : 'bg-muted/30 text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            {showWsBadge && (
              <span className={cn('text-[8px] font-bold px-1 py-0.5 rounded text-white leading-none shrink-0', tab.wsColor)}>
                {tab.wsLabel}
              </span>
            )}
            <FileText className="h-3 w-3 shrink-0 opacity-40" />
            <span className="truncate max-w-[100px]">{tab.label}</span>
            <button
              onClick={e => e.stopPropagation()}
              className="ml-0.5 opacity-0 group-hover:opacity-60 hover:!opacity-100 text-muted-foreground hover:text-foreground transition-opacity shrink-0"
              aria-label="Close tab"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
        )
      })}

      <button className="flex items-center px-2 text-muted-foreground/50 hover:text-foreground transition-colors shrink-0">
        <span className="text-sm leading-none">+</span>
      </button>

      {/* Annotation */}
      <div className="flex items-center px-3 ml-auto shrink-0">
        <span className="text-[10px] text-muted-foreground/40 italic whitespace-nowrap">
          Inbox = workspace-agnostic, closeable | doc clicks open in workspace group
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function InboxSplitTest() {
  const [threads, setThreads] = useState<Thread[]>(INITIAL_THREADS)
  const [workspaces, setWorkspaces] = useState<Workspace[]>(INITIAL_WORKSPACES)
  const [kindFilter, setKindFilter] = useState<KindFilter>('all')
  const [activeExtension, setActiveExtension] = useState<string | null>(null)
  const [activeId, setActiveId] = useState<string>('v1')
  const [dense, setDense] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeNav, setActiveNav] = useState('inbox')
  const [activeTabId, setActiveTabId] = useState('inbox')
  // Tracks which thread the right-rail chat is currently scoped to.
  // null = chat is not linked to a specific inbox thread.
  const [chatLinkedThreadId, setChatLinkedThreadId] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const [banner, setBanner] = useState<
    | { type: 'archive'; threadId: string }
    | { type: 'snooze'; threadId: string; label: string; hint: string }
    | null
  >(null)
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function showBanner(b: typeof banner) {
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current)
    setBanner(b)
    bannerTimerRef.current = setTimeout(() => setBanner(null), 5000)
  }

  const activeWsIds = new Set(workspaces.filter(w => w.active).map(w => w.id))

  function toggleWorkspace(id: string) {
    setWorkspaces(prev => prev.map(w => w.id === id ? { ...w, active: !w.active } : w))
  }

  function archiveThread(id: string) {
    setThreads(prev => prev.map(t => t.id === id ? { ...t, status: 'archived' as Status } : t))
    showBanner({ type: 'archive', threadId: id })
    const next = threads.find(t => t.id !== id && activeWsIds.has(t.workspaceId) && t.status !== 'archived' && t.status !== 'snoozed')
    if (next && id === activeId) setActiveId(next.id)
  }

  function snoozeThread(id: string, label: string, hint: string) {
    setThreads(prev => prev.map(t => t.id === id ? { ...t, status: 'snoozed' as Status, snoozedUntil: hint } : t))
    showBanner({ type: 'snooze', threadId: id, label, hint })
    const next = threads.find(t => t.id !== id && activeWsIds.has(t.workspaceId) && t.status !== 'archived' && t.status !== 'snoozed')
    if (next && id === activeId) setActiveId(next.id)
  }

  function unarchiveThread(id: string) {
    setThreads(prev => prev.map(t => t.id === id ? { ...t, status: 'read' as Status, snoozedUntil: undefined } : t))
    setBanner(null)
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        setSearchOpen(true)
        setTimeout(() => searchRef.current?.focus(), 50)
      }
      if (e.key === 'Escape' && searchOpen) { setSearchOpen(false); setSearchQuery('') }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [searchOpen])

  const visible = threads.filter(t => {
    // Archived view: show all archived regardless of workspace
    if (kindFilter === 'archived') return t.status === 'archived'
    // All other views: respect active workspace filter
    if (!activeWsIds.has(t.workspaceId)) return false
    if (kindFilter === 'snoozed') return t.status === 'snoozed'
    if (t.status === 'archived' || t.status === 'snoozed') return false
    if (kindFilter !== 'all' && t.kind !== kindFilter) return false
    // Extension sub-filter: only show threads from the selected extension
    if (kindFilter === 'extension' && activeExtension && t.extensionId !== activeExtension) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return t.title.toLowerCase().includes(q) || t.preview.toLowerCase().includes(q) || t.source.toLowerCase().includes(q)
    }
    return true
  })

  const activeThread = threads.find(t => t.id === activeId) ?? threads[0]

  return (
    <div
      data-testid="inbox-split-prototype"
      className="flex flex-col h-screen bg-background text-foreground overflow-hidden font-sans"
    >
      {/* Tab bar � inbox is a pinned persistent tab before workspace groups */}
      <MockTabBar activeTabId={activeTabId} onTabChange={setActiveTabId} />

      <div className="flex flex-1 min-h-0 overflow-hidden">
      <BrowseRail
        threads={threads}
        workspaces={workspaces}
        activeNav={activeNav}
        activeFilter={kindFilter}
        activeExtension={activeExtension}
        onNavChange={setActiveNav}
        onFilterChange={(f) => { setKindFilter(f); setActiveNav('inbox') }}
        onExtensionChange={setActiveExtension}
        onToggleWorkspace={toggleWorkspace}
      />

      {/* Feed pane */}
      <div
        data-testid="inbox-feed"
        className="flex flex-col overflow-hidden shrink-0 border-r border-border"
        style={{ width: 300 }}
      >
        <div className="flex items-center gap-2 px-3 h-10 border-b border-border shrink-0">
          <span className="font-semibold text-sm flex-1 text-foreground">
            {kindFilter === 'archived' ? 'Archive' : kindFilter === 'snoozed' ? 'Snoozed' : 'Inbox'}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton icon={<MoreVertical className="h-4 w-4" />} label="Inbox options" variant="ghost" size="sm" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">Layout</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setDense(false)} className={cn(!dense && 'text-primary')}>
                <AlignJustify className="mr-2 h-4 w-4" /> Detailed
                {!dense && <span className="ml-auto text-xs opacity-50">on</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDense(true)} className={cn(dense && 'text-primary')}>
                <List className="mr-2 h-4 w-4" /> Dense
                {dense && <span className="ml-auto text-xs opacity-50">on</span>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 50) }}>
                <Search className="mr-2 h-4 w-4" /> Search inbox
                <span className="ml-auto text-xs text-muted-foreground">Cmd+F</span>
              </DropdownMenuItem>
              <DropdownMenuItem><RefreshCw className="mr-2 h-4 w-4" /> Refresh</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem><CheckCheck className="mr-2 h-4 w-4" /> Mark all read</DropdownMenuItem>
              <DropdownMenuItem className="text-muted-foreground">
                <Archive className="mr-2 h-4 w-4" /> Archive read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Extension sub-filter bar � only shown when Extensions view is active */}
        {kindFilter === 'extension' && (() => {
          const extShortcuts = EXTENSION_SHORTCUTS.filter(sc => sc.workspaceId && activeWsIds.has(sc.workspaceId))
          if (extShortcuts.length === 0) return null
          return (
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border shrink-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setActiveExtension(null)}
                className={cn(
                  'flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap transition-colors shrink-0 border',
                  !activeExtension ? 'bg-primary/10 text-primary border-primary/20' : 'text-muted-foreground hover:bg-muted hover:text-foreground border-transparent'
                )}
              >
                All
              </button>
              {extShortcuts.map(sc => {
                const Icon = sc.icon
                const active = activeExtension === sc.extensionId
                return (
                  <button
                    key={sc.extensionId}
                    onClick={() => setActiveExtension(sc.extensionId ?? null)}
                    className={cn(
                      'flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap transition-colors shrink-0 border',
                      active ? 'bg-primary/10 text-primary border-primary/20' : 'text-muted-foreground hover:bg-muted hover:text-foreground border-transparent'
                    )}
                  >
                    <Plug className="h-2.5 w-2.5 shrink-0" />
                    <span>{sc.label}</span>
                  </button>
                )
              })}
            </div>
          )
        })()}

        {banner && (
          banner.type === 'snooze'
            ? <SnoozeBanner label={banner.label} hint={banner.hint} onUndo={() => { if (banner) unarchiveThread(banner.threadId) }} />
            : <ArchiveBanner onUndo={() => { if (banner) unarchiveThread(banner.threadId) }} />
        )}

        {/* Workspace scope warning */}
        <WorkspaceScopePill workspaces={workspaces} />

        {searchOpen && (
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/20 shrink-0">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground/60 shrink-0 select-none">search:</span>
            <input
              ref={searchRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="filter threads..."
              className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground/40 min-w-0"
            />
            <button onClick={() => { setSearchOpen(false); setSearchQuery('') }} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {kindFilter === 'archived' && visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-2 text-center px-6 py-12">
            <Archive className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">Archive is empty</p>
            <p className="text-xs text-muted-foreground/60">Archived threads live here, not deleted.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto min-h-0">
            {kindFilter === 'archived' && visible.length > 0 && (
              <div className="px-3 py-2 border-b border-border bg-muted/20">
                <p className="text-[10px] text-muted-foreground/60">
                  {visible.length} archived thread{visible.length !== 1 ? 's' : ''} -- not deleted, fully searchable
                </p>
              </div>
            )}
            {visible.length === 0 && kindFilter !== 'archived' && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">No threads in this view.</div>
            )}
            {visible.map(t => (
              <ThreadRow
                key={t.id}
                thread={t}
                active={t.id === activeId}
                dense={dense}
                onClick={() => setActiveId(t.id)}
                onArchive={archiveThread}
                onSnooze={snoozeThread}
                workspace={workspaces.find(w => w.id === t.workspaceId)}
              />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 px-3 py-1.5 border-t border-border bg-background shrink-0">
          <span className="text-[10px] text-muted-foreground/50">Up/Down</span>
          <span className="text-[10px] text-muted-foreground/50">E archive</span>
          <span className="text-[10px] text-muted-foreground/50">H snooze</span>
          <div className="flex-1" />
          <span className="text-[10px] text-muted-foreground/40 tabular-nums">
            {visible.length} / {threads.filter(t => activeWsIds.has(t.workspaceId) && t.status === 'unread').length} unread
          </span>
        </div>
      </div>

      {/* Reader */}
      <div
        data-panel-secondary
        data-panel-type="inbox-reader"
        className="flex flex-col flex-1 min-w-0 overflow-hidden"
      >
        <ReaderPane
          thread={activeThread}
          onArchive={archiveThread}
          onSnooze={snoozeThread}
          onUnarchive={unarchiveThread}
          chatLinkedThreadId={chatLinkedThreadId}
          onOpenInChat={(id) => setChatLinkedThreadId(id || null)}
        />
      </div>

      {/* Right-rail agent chat */}
      <RightRailChat
        thread={activeThread}
        chatLinkedThreadId={chatLinkedThreadId}
        onOpenInChat={(id) => setChatLinkedThreadId(id || null)}
        onFocusInbox={() => setActiveId(activeThread.id)}
      />
      </div>
    </div>
  )
}
